/**
 * JOB DETAIL SCREEN
 *
 * Full job details: product info, pickup/delivery addresses with map link,
 * contacts (when job accepted), payout, pickup window, actions.
 * Full pickup address shown exactly from WooCommerce.
 * Training gating happens naturally when driver tries to accept.
 */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  ActivityIndicator, Platform, Modal, Image, Animated, Dimensions, Linking,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { getDriverTips } from '../../utils/driverTips';
import { spacing, radii, fonts, RD } from '../../theme/colors';
import { RouteMap, RouteInfoBar } from '../../components/RouteMap';
import WeatherCard from '../../components/WeatherCard';
import { LightbulbBody } from '../../components/LightbulbBody';
import { prefetchJobBrief, markJobBriefViewed } from '../../services/jobBrief';
import ImageLightbox from '../../components/ImageLightbox';
import { api } from '../../services/api';
import { ensureLocationEnabled } from '../../services/location';
import { MOCK_AVAILABLE_JOBS, MOCK_ACTIVE_JOB, MOCK_COURSES } from '../../data/mockData';
import { getTrainingCategoryLabel, isDriverCertifiedForJob, getCourseIdForCategory, mapToTrainingCategory, needsPalletizingCert } from '../../utils/categoryMapping';
import { pairPickupWithDelivery } from '../../utils/pickupWindow';
import { bufferedPickup } from '../../utils/windows';
import { RootState } from '../../store';
import { PREDISPATCH_ENABLED } from '../../config/flags';
import { CoiCard } from '../../components/CoiCard';

const WIN = Dimensions.get('window');

export function JobDetailScreen({ route, navigation }: any) {
  const { jobId, job: passedJob } = route.params;
  const [jobState, setJob] = useState<any>(passedJob || null);
  let job = jobState;
  const [showTrainingGate, setShowTrainingGate] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [routeInfo, setRouteInfo] = useState<{ distanceMiles: number; durationMinutes: number } | null>(null);

  const certifications = useSelector((s: RootState) => s.auth.certifications) ?? [];
  const driver = useSelector((s: RootState) => s.auth.driver);
  // Fleet state driven by job._isFleetManager from backend
  const isFleetManager = !!(job as any)?._isFleetManager;
  const [fleetMembers, setFleetMembers] = useState<any[]>([]);
  const [showFleetAssign, setShowFleetAssign] = useState(false);
  const [assigningFleet, setAssigningFleet] = useState(false);
  const [acceptedJob, setAcceptedJob] = useState<any>(null);
  // Pickup-truck heavy-rain acknowledgement before accept (informational, never blocks).
  const [wxConfirm, setWxConfirm] = useState<null | { reason: string; when: string; severe: boolean }>(null);
  // reassignMode = the fleet picker is being opened to swap drivers on an
  // already-assigned job (not as part of the initial accept flow). Suppresses
  // navigation to ActiveJob on dismiss/submit.
  const [reassignMode, setReassignMode] = useState(false);

  // Update fleet members from job data
  useEffect(() => {
    if (job?._fleetMembers) setFleetMembers(job._fleetMembers);
  }, [job?._fleetMembers]);

  useEffect(() => {
    // Always fetch full job data from API (passedJob may be missing fields like deliveryNotes)
    const id = jobId || passedJob?.id;
    if (!id) return;
    api.get(`/jobs/${id}`)
      .then(r => setJob((prev: any) => ({ ...prev, ...r.data })))
      .catch(() => {
        if (!passedJob) {
          const mock = MOCK_AVAILABLE_JOBS.find(j => j.id === jobId) || MOCK_ACTIVE_JOB;
          if (mock) setJob(mock);
        }
      });
  }, [jobId, passedJob?.id]);

  const [accepting, setAccepting] = useState(false);
  const [showTips, setShowTips] = useState(false);
  // The lightbulb's content (item brief, manufacturer specs, category guide) is fetched by
  // components/LightbulbBody when the sheet opens; the brief is prefetched below.
  const [showDetailedTips, setShowDetailedTips] = useState(false);
  const [tipsViewed, setTipsViewed] = useState(false);
  const tipsPulse = useRef(new Animated.Value(1)).current;
  // (flag-gated) Full-screen swipeable lightbox for the combined listing +
  // pre-pickup inspection gallery surfaced on the Job Details screen.
  const [galleryLightbox, setGalleryLightbox] = useState<{ list: string[]; index: number } | null>(null);
  // (flag-gated) Reminder-card pulse that points at the blue bulb FAB.
  const reminderPulse = useRef(new Animated.Value(1)).current;
  // Hardcoded fallback tips — always available so drivers never lose instructions offline.
  const fallbackTips = useMemo(() => getDriverTips(job?.productTitle || '', job?.categorySlug || ''), [job?.productTitle, job?.categorySlug]);

  // Contact info is only available on accepted/active jobs. For non-active jobs we
  // render off a sanitized copy that omits contact fields (backend already strips
  // these, but WooCommerce fallback data might still include them). Never mutate
  // the job state object during render.
  const displayJob = useMemo(() => {
    if (!job) return job;
    const active = !['AVAILABLE', 'COMPLETED', 'CANCELLED'].includes(job.status);
    if (active) return job;
    const { buyerPhone, buyerEmail, sellerPhone, sellerEmail, ...rest } = job;
    return rest;
  }, [job]);

  // LIVE category instructions pulled from the backend so admin edits show up.
  // Falls back to fallbackTips if the fetch fails or returns nothing.
  const [liveInstructions, setLiveInstructions] = useState<{
    label?: string;
    specs?: string[];
    precautions?: string[];
    steps?: string[];
    videos?: { title: string; url: string }[];
  } | null>(null);

  useEffect(() => {
    const slug = job?.categorySlug;
    if (!slug) return;
    let cancelled = false;
    api.get(`/category-instructions/${encodeURIComponent(slug)}`, {
      params: { productTitle: job?.productTitle || '' },
    })
      .then(r => { if (!cancelled) setLiveInstructions(r.data || null); })
      .catch(() => { if (!cancelled) setLiveInstructions(null); });
    return () => { cancelled = true; };
  }, [job?.categorySlug, job?.productTitle]);

  useEffect(() => {
    const id = jobId || passedJob?.id;
    if (!id) return;
    AsyncStorage.getItem(`tips_viewed_${id}`).then(val => {
      if (val === 'true') setTipsViewed(true);
    }).catch(() => {});
  }, [jobId, passedJob?.id]);
  // Opened on another phone (or before a reinstall): the server remembers.
  useEffect(() => { if (jobState?.briefViewedAt) setTipsViewed(true); }, [jobState?.briefViewedAt]);
  // Warm the lightbulb's brief in the background once the job is known. Never awaited, so
  // the job screen renders exactly as fast as before.
  useEffect(() => { prefetchJobBrief(jobId || passedJob?.id); }, [jobId, passedJob?.id]);
  const openTips = () => {
    const id = jobId || passedJob?.id;
    setShowTips(true);
    setTipsViewed(true);
    markJobBriefViewed(id);
  };

  useEffect(() => {
    if (tipsViewed) return;
    const anim = Animated.loop(
      Animated.sequence([
        Animated.timing(tipsPulse, { toValue: 0.2, duration: 600, useNativeDriver: true }),
        Animated.timing(tipsPulse, { toValue: 1, duration: 600, useNativeDriver: true }),
      ])
    );
    anim.start();
    return () => anim.stop();
  }, [tipsViewed, tipsPulse]);

  // (flag-gated) Gentle pulse on the "Check Helpful Hints" reminder card so it
  // visually points at the blue bulb FAB. Stops once the tips have been viewed.
  useEffect(() => {
    if (!PREDISPATCH_ENABLED || tipsViewed) return;
    const anim = Animated.loop(
      Animated.sequence([
        Animated.timing(reminderPulse, { toValue: 0.55, duration: 750, useNativeDriver: true }),
        Animated.timing(reminderPulse, { toValue: 1, duration: 750, useNativeDriver: true }),
      ])
    );
    anim.start();
    return () => anim.stop();
  }, [tipsViewed, reminderPulse]);

  if (!job) return <View style={s.loadingWrap}><ActivityIndicator size="large" color={RD.ink} /></View>;

  const isActive = !['AVAILABLE', 'COMPLETED', 'CANCELLED'].includes(job.status);

  // From here on, render off the sanitized copy so contact info stays hidden for
  // non-active jobs without mutating React state during render.
  job = displayJob;

  const statusColor = job.status === 'COMPLETED' ? RD.money
    : job.status === 'CANCELLED' ? RD.danger
    : isActive ? RD.amber : RD.info;
  const statusSoft = job.status === 'COMPLETED' ? RD.moneySoft
    : job.status === 'CANCELLED' ? RD.dangerSoft
    : isActive ? RD.amberSoft : RD.infoSoft;

  const categoryCertified = isDriverCertifiedForJob(job.categorySlug || 'general', certifications);
  const palletizingMissing = needsPalletizingCert(job) && !certifications.includes('palletizing');
  const isCertified = categoryCertified && !palletizingMissing;
  const categoryLabel = getTrainingCategoryLabel(job.categorySlug || 'general');

  // Fleet member = a driver who belongs to a manager's fleet (not the manager).
  // Fleet members never see job payout amounts.
  const isFleetMember = !!(driver as any)?.fleetManagerId && !isFleetManager;

  // Crew headcount label: "1 driver + N helper(s)". Drivers bring their own helpers.
  const crewLabel = (() => {
    const total = job.helpersNeeded || 1;
    const helpers = Math.max(0, total - 1);
    return `1 driver${helpers > 0 ? ` + ${helpers} helper${helpers > 1 ? 's' : ''}` : ''}`;
  })();

  // The inline "How to move it" card on this screen: live steps, else the hardcoded tips.
  // (The lightbulb itself renders components/LightbulbBody.)
  const liveSteps = (liveInstructions?.steps || []).filter(Boolean);
  const stepList: string[] = liveSteps.length > 0 ? liveSteps : (fallbackTips?.tips || []);

  // Listing photos parsed once. The raw field may be an array or a JSON string
  // (including "[]"), so gate the section on the parsed array's real length.
  const _allProductMedia: string[] = (() => {
    const pi = job?.productImages;
    if (!pi) return [];
    if (Array.isArray(pi)) return pi.filter(Boolean);
    try { const p = JSON.parse(pi); return Array.isArray(p) ? p.filter(Boolean) : []; } catch { return []; }
  })();
  const isVideoUrl = (u: string) => /\.(mp4|mov|webm|m4v|mkv|avi|quicktime)(\?|$)/i.test(u || '') || (u || '').includes('/video/upload/');
  // Photos only for the image gallery/lightbox; videos render as a separate "Watch video" link.
  const productImages: string[] = _allProductMedia.filter((u) => !isVideoUrl(u));
  const productVideos: string[] = _allProductMedia.filter((u) => isVideoUrl(u));

  // Pre-pickup seller inspection photos (separate from WC listing photos). Same
  // tolerant parse — may be an array or a JSON string.
  const inspectionPhotos: string[] = (() => {
    const ip = job?.inspectionPhotos;
    if (!ip) return [];
    if (Array.isArray(ip)) return ip.filter(Boolean);
    try { const p = JSON.parse(ip); return Array.isArray(p) ? p.filter(Boolean) : []; } catch { return []; }
  })();

  // (flag-gated) Combined gallery: listing photos first, then pre-pickup
  // inspection photos, in ONE swipeable lightbox.
  const combinedGallery: string[] = [...productImages, ...inspectionPhotos];

  // Whether the lightbulb has anything worth showing at all.
  // Always true now: every job has at least its crew line in "Before you walk in", and the
  // driver must open the bulb before Start (ActiveJobScreen), so it can never be hidden.
  const hasHints = true;

  // The actual accept (runs after the cert/location/weather checks pass or are acknowledged).
  const doAccept = async () => {
    setAccepting(true);
    try {
      await api.post(`/jobs/${job.id}/accept`);
      const accepted = { ...job, status: 'ACCEPTED' };
      // Fleet manager: show assignment modal instead of navigating directly
      if (isFleetManager && fleetMembers.length > 0) {
        setAcceptedJob(accepted);
        setShowFleetAssign(true);
      } else {
        navigation.navigate('ActiveJob', { jobId: job.id, job: accepted });
      }
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Could not accept job');
    }
    setAccepting(false);
  };

  const handleAcceptPress = async () => {
    // Confirm to prevent accidental accepts (a common tap-through). Web confirm — drivers are on the PWA.
    const itemName = String(job?.productTitle || 'this job').replace(/&amp;/g, '&').replace(/\s*[-–]\s*[A-Za-z .'-]+,\s*[A-Z]{2}\s*$/, '');
    if (typeof window !== 'undefined' && window.confirm && !window.confirm(`Accept "${itemName}"?\n\nOnly accept if you can complete both the pickup and the delivery. You'll be responsible for scheduling and finishing this job.`)) return;
    if (!isCertified) {
      setShowTrainingGate(true);
      return;
    }
    setAccepting(true);
    // HARD GATE: location must be enabled to accept a job (prompts + posts a fresh fix,
    // marking the driver locationGranted so live tracking works from the start).
    const locOk = await ensureLocationEnabled();
    if (!locOk) {
      setAccepting(false);
      alert('Please enable location to accept jobs. Commonplace needs your location for live delivery tracking. Turn it on in your settings and try again.');
      return;
    }
    // PICKUP-TRUCK WEATHER NOTE: if the driver has an open-bed truck and heavy rain/storms are
    // forecast during the pickup/delivery hours, surface a one-tap acknowledgement (covers ready).
    // Purely informational — never blocks the accept.
    try {
      const { data } = await api.get(`/jobs/${job.id}/weather-accept-check`);
      if (data?.flag) { setAccepting(false); setWxConfirm({ reason: data.reason || 'Heavy rain', when: data.when || 'pickup', severe: !!data.severe }); return; }
    } catch { /* weather check is best-effort; never blocks accept */ }
    await doAccept();
  };

  const handleFleetAssign = async (fleetDriverId: string) => {
    if (!acceptedJob) return;
    setAssigningFleet(true);
    try {
      await api.post(`/jobs/${acceptedJob.id}/assign-fleet`, { fleetDriverId });
      const newAssigneeId = fleetDriverId === 'self' ? driver?.id : fleetDriverId;
      const assignedJob = { ...acceptedJob, assignedFleetDriverId: newAssigneeId };
      setShowFleetAssign(false);
      if (reassignMode) {
        // In-place reassignment — update local job state, stay on this screen.
        setJob((prev: any) => prev ? { ...prev, assignedFleetDriverId: newAssigneeId } : prev);
        setReassignMode(false);
        setAcceptedJob(null);
      } else if (fleetDriverId === 'self') {
        navigation.navigate('ActiveJob', { jobId: acceptedJob.id, job: assignedJob });
      } else {
        // Initial assignment to someone else — back to the job board.
        navigation.goBack();
      }
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Could not assign driver');
    }
    setAssigningFleet(false);
  };

  // Fleet manager: swap who's assigned on an already-assigned, not-yet-complete job.
  const canReassign = isFleetManager
    && !!job.assignedFleetDriverId
    && !['COMPLETED', 'CANCELLED'].includes(job.status)
    && fleetMembers.length > 0;
  const currentAssigneeName = canReassign
    ? (job.assignedFleetDriverId === driver?.id
        ? 'You'
        : (fleetMembers.find((m: any) => m.id === job.assignedFleetDriverId)?.name || 'Fleet driver'))
    : null;
  const openReassign = () => {
    setAcceptedJob(job);
    setReassignMode(true);
    setShowFleetAssign(true);
  };

  const handleGoToTraining = () => {
    setShowTrainingGate(false);
    const hasDriverCert = certifications.includes('driver-certification');

    // If driver hasn't completed General Driver Certification, send there first
    if (!hasDriverCert) {
      const trainingCat = mapToTrainingCategory(job.categorySlug || 'general');
      const categoryCourseId = getCourseIdForCategory(trainingCat);
      navigation.navigate('Quiz', {
        courseId: 'course-driver-cert',
        title: 'General Driver Certification',
        returnToCourse: categoryCourseId || undefined,
        fromJobId: job.id,
        fromJob: job,
      });
      return;
    }

    // Category done, palletizing missing: straight to the palletizing course.
    if (categoryCertified && palletizingMissing) {
      navigation.navigate('Quiz', { courseId: 'course-palletizing', title: 'Palletizing Certification', fromJobId: job.id, fromJob: job });
      return;
    }

    // Driver has general cert — send directly to category quiz
    const slug = job.categorySlug || job.productCategory || job._tms_category || (Array.isArray(job.categories) && job.categories[0]) || 'general';
    const trainingCat = mapToTrainingCategory(slug);
    const courseId = getCourseIdForCategory(trainingCat);
    if (courseId) {
      const course = MOCK_COURSES.find(c => c.id === courseId);
      navigation.navigate('Quiz', { courseId, title: course?.title || 'Training', fromJobId: job.id, fromJob: job });
    } else {
      // No category-specific course found — fall back to driver-cert quiz
      navigation.navigate('Quiz', { courseId: 'course-driver-cert', title: 'General Driver Certification', fromJobId: job.id, fromJob: job });
    }
  };

  return (
    <View style={s.container}>
      {/* Header */}
      <View style={s.header}>
        <TouchableOpacity onPress={() => {
          if (galleryLightbox) { setGalleryLightbox(null); }
          else if (showTips) { setShowTips(false); setShowDetailedTips(false); }
          else if (showImageModal) { setShowImageModal(false); }
          else if (showTrainingGate) { setShowTrainingGate(false); }
          else { navigation.goBack(); }
        }} style={s.backBtn} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }} activeOpacity={0.7}>
          <Ionicons name="chevron-back" size={24} color={RD.text} />
        </TouchableOpacity>
        <View style={s.headerTitleWrap}>
          <Text style={s.headerTitle}>Job Details</Text>
          <Text style={s.headerSub}>Review and accept</Text>
        </View>
        <View style={{ width: 24 }} />
      </View>

      <ScrollView contentContainerStyle={s.content} showsVerticalScrollIndicator={false}>
        {/* (1) Product card — title, condition, category + crew pills */}
        <View style={s.topCard}>
          <View style={[s.statusBadge, { backgroundColor: statusSoft }]}>
            <View style={[s.statusDot, { backgroundColor: statusColor }]} />
            <Text style={[s.statusText, { color: statusColor }]}>{job.status?.replace(/_/g, ' ')}</Text>
          </View>
          {/* Title row with thumbnail */}
          <View style={s.titleRow}>
            {job.productImages?.[0] && (
              <TouchableOpacity onPress={() => setShowImageModal(true)} activeOpacity={0.8}>
                <Image source={{ uri: job.productImages[0] }} style={s.productThumb} resizeMode="cover" />
              </TouchableOpacity>
            )}
            <Text style={[s.productTitle, job.productImages?.[0] ? { flex: 1 } : undefined]}>
              {job.displayTitle || (() => {
                // Human order number only; a job without one (a storage pickup) shows its title.
                const orderNum = job.orderNumber || null;
                if (job.productTitle) {
                  const short = job.productTitle
                    .replace(/\s*[-–|]\s*(Like New|Used|New|Good|Excellent|Fair|Great|Open Box).*$/i, '')
                    .replace(/,\s*([\d]+[WHD"']|[\d]+\s*(in|inch|cm|mm)).*$/i, '')
                    .trim();
                  return orderNum ? `#${orderNum} – ${short}` : short;
                }
                return orderNum ? `Order #${orderNum} – ${categoryLabel}` : categoryLabel;
              })()}
            </Text>
          </View>
          {/* Condition / spec subline */}
          {!!job.condition && (
            <Text style={s.productSub}>{job.condition}</Text>
          )}
          {/* Category + crew pills */}
          <View style={s.pillRow}>
            <View style={s.categoryPill}>
              <Text style={s.categoryText}>{categoryLabel}</Text>
            </View>
            <View style={s.crewPill}>
              <Ionicons name="people-outline" size={13} color={RD.textSec} />
              <Text style={s.crewPillText}>{crewLabel}</Text>
            </View>
            {(routeInfo?.distanceMiles || job._distanceMiles) && (
              <View style={s.distanceBadge}>
                <Ionicons name="location-outline" size={13} color={RD.textFaint} />
                <Text style={s.distanceText}>{routeInfo ? `${routeInfo.distanceMiles} mi` : `${job._distanceMiles.toFixed(1)} mi`}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Payout at the TOP — total only, never a fee breakdown. Hidden for fleet members. */}
        {!isFleetMember && (
          <View style={s.payoutCard}>
            <Text style={s.payoutLabelInline}>Your payout</Text>
            <Text style={s.payoutAmountInline}>${Math.round(job.payoutAmount ?? 0).toLocaleString('en-US')}</Text>
          </View>
        )}

        {/* (flag-gated · ADD A) Reminder card — points at the blue bulb FAB. */}
        {PREDISPATCH_ENABLED && hasHints && (
          <Animated.View style={{ opacity: !tipsViewed ? reminderPulse : 1 }}>
            <TouchableOpacity
              style={s.hintsReminderCard}
              activeOpacity={0.8}
              onPress={openTips}
            >
              <View style={s.hintsReminderIcon}>
                <Ionicons name="bulb" size={18} color={RD.info} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={s.hintsReminderTitle}>For more info, check the lightbulb (bottom right)</Text>
                <Text style={s.hintsReminderSub}>Crew, stairs, what the seller reported, and what drivers before you ran into</Text>
              </View>
              <Ionicons name="arrow-forward-circle" size={22} color={RD.info} />
            </TouchableOpacity>
          </Animated.View>
        )}

        {/* (flag-gated · ADD B) Listing + pre-pickup inspection photos in ONE
            swipeable gallery with a full-screen lightbox. */}
        {PREDISPATCH_ENABLED && combinedGallery.length > 0 && (
          <View style={s.galleryCard}>
            <View style={s.galleryCardHead}>
              <Ionicons name="images-outline" size={16} color={RD.maroon} />
              <Text style={s.galleryCardTitle}>Photos</Text>
              <Text style={s.galleryCardHint}>tap to enlarge and swipe</Text>
            </View>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginHorizontal: -4 }}>
              {combinedGallery.map((uri: string, i: number) => (
                <TouchableOpacity key={i} onPress={() => setGalleryLightbox({ list: combinedGallery, index: i })} activeOpacity={0.85}>
                  <Image source={{ uri }} style={s.galleryThumb} resizeMode="cover" />
                  <View style={s.galleryZoomHint}><Ionicons name="expand-outline" size={12} color="#fff" /></View>
                  {i >= productImages.length && (
                    <View style={s.galleryTag}><Text style={s.galleryTagText}>Pre-pickup</Text></View>
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        )}

        {/* (2) Route map area with Navigate button (RouteMap renders its own Navigate) */}
        {job.pickupAddress && job.deliveryAddress && (
          <>
            <RouteMap
              pickupAddress={job.pickupAddress}
              deliveryAddress={job.deliveryAddress}
              height={200}
              onRouteInfo={setRouteInfo}
            />
            {routeInfo && <RouteInfoBar distanceMiles={routeInfo.distanceMiles} durationMinutes={routeInfo.durationMinutes} />}
          </>
        )}

        {/* Pickup & Delivery location cards (kept; surfaces contacts once active) */}
        {(() => {
          // The location-local scheduled day, so the weather features the actual pickup/delivery
          // day (e.g. "tomorrow") instead of just current conditions.
          const wxDay = (() => {
            if (!job.scheduledWindowStart) return undefined;
            try { return new Intl.DateTimeFormat('en-CA', { timeZone: job.pickupTimezone || undefined, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(job.scheduledWindowStart)); }
            catch { return String(job.scheduledWindowStart).slice(0, 10); }
          })();
          return (
            <>
              <SectionCard title="Pickup Location" icon="arrow-up-circle-outline" iconColor={RD.maroon}>
                <InfoRow icon="location" value={job.pickupAddress} />
                {(job.pickupLat != null && job.pickupLng != null) ? <WeatherCard lat={job.pickupLat} lng={job.pickupLng} label={wxDay ? 'Pickup weather' : 'Pickup weather (now)'} highlightDate={wxDay} /> : null}
                {isActive && job.sellerName && (
                  <>
                    <InfoRow icon="person" value={job.sellerName} />
                    <InfoRow icon="chatbubble" value="Message Seller" onPress={() => navigation.navigate('Messaging', { jobId: job.id, job, initialRecipient: 'SELLER' })} />
                  </>
                )}
              </SectionCard>

              <SectionCard title="Delivery Location" icon="arrow-down-circle-outline" iconColor={RD.ink}>
                <InfoRow icon="location" value={job.deliveryAddress} />
                {(job.deliveryLat != null && job.deliveryLng != null) ? <WeatherCard lat={job.deliveryLat} lng={job.deliveryLng} label={wxDay ? 'Delivery weather' : 'Delivery weather (now)'} highlightDate={wxDay} /> : null}
                {isActive && job.buyerName && (
                  <>
                    <InfoRow icon="person" value={job.buyerName} />
                    <InfoRow icon="chatbubble" value="Message Buyer" onPress={() => navigation.navigate('Messaging', { jobId: job.id, job, initialRecipient: 'BUYER' })} />
                  </>
                )}
              </SectionCard>
            </>
          );
        })()}

        {/* (3) Pickup-windows card — seller availability. NEVER show delivery windows. */}
        {(() => {
          const paired = pairPickupWithDelivery(job.pickupWindows, job.deliveryWindows, job.scheduledWindowStart);
          if (paired.length === 0) return null;
          const tzOpts: Intl.DateTimeFormatOptions = job.pickupTimezone ? { timeZone: job.pickupTimezone } : {};
          const title = paired.length === 1 ? 'Available pickup window' : 'Available pickup windows';
          return (
            <SectionCard title={title} icon="time-outline" iconColor={RD.maroon}>
              {paired.map((p, i) => {
                const bp = bufferedPickup(p.pickupStart, p.pickupEnd);
                return (
                <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 4 }}>
                  <Ionicons name="calendar-outline" size={15} color={p.isSelected ? RD.money : RD.maroon} />
                  <Text style={{ flex: 1, fontSize: 14, color: p.isSelected ? RD.money : RD.textSec, fontWeight: p.isSelected ? '700' : '600' }}>
                    {bp.start!.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', ...tzOpts })}
                    {' · '}
                    {bp.start!.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...tzOpts })}
                    {bp.end ? ` – ${bp.end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...tzOpts })}` : ''}
                  </Text>
                  {p.isSelected && <Ionicons name="checkmark-circle" size={16} color={RD.money} />}
                </View>
              ); })}
              {!isActive && <Text style={s.windowHint}>Pick your window after accepting the job.</Text>}
            </SectionCard>
          );
        })()}

        {/* Legacy pickup-windows-only block (kept for jobs where deliveryWindows hasn't been stitched but pickupWindows exists) */}
        {(!job.deliveryWindows || job.deliveryWindows.length === 0) && job.pickupWindows?.length > 0 && (
          <SectionCard title={job.pickupWindows.length === 1 ? 'Available pickup window' : 'Available pickup windows'} icon="time-outline" iconColor={RD.maroon}>
            {job.pickupWindows.map((pw: any, i: number) => {
              const bp = bufferedPickup(pw.start, pw.end);
              const tzOpts: Intl.DateTimeFormatOptions = job.pickupTimezone ? { timeZone: job.pickupTimezone } : {};
              return (
                <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 4 }}>
                  <Ionicons name="calendar-outline" size={15} color={RD.maroon} />
                  <Text style={{ flex: 1, fontSize: 14, color: RD.textSec, fontWeight: '600' }}>
                    {bp.start!.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', ...tzOpts })}
                    {' · '}
                    {bp.start!.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...tzOpts })}
                    {bp.end ? ` – ${bp.end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...tzOpts })}` : ''}
                  </Text>
                </View>
              );
            })}
            {!isActive && <Text style={s.windowHint}>Pick your window after accepting the job.</Text>}
          </SectionCard>
        )}

        {/* Fleet Assignment — fleet manager can swap who's assigned */}
        {canReassign && (
          <SectionCard title="Fleet Assignment" icon="people-outline" iconColor={RD.maroon}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 4 }}>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 12, color: RD.textFaint }}>Assigned to</Text>
                <Text style={{ fontSize: 15, fontWeight: '700', color: RD.text }}>{currentAssigneeName}</Text>
              </View>
              <TouchableOpacity
                onPress={openReassign}
                style={{ backgroundColor: RD.ink, paddingHorizontal: 16, paddingVertical: 11, borderRadius: 12 }}
                activeOpacity={0.8}
              >
                <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>Reassign</Text>
              </TouchableOpacity>
            </View>
          </SectionCard>
        )}

        {/* (4) Crew card — headcount only; driver brings own helpers */}
        {job.helpersNeeded && (
          <View style={s.crewCard}>
            <View style={s.crewIconWrap}>
              <Ionicons name="people-outline" size={20} color={RD.maroon} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={s.crewCardTitle}>Crew needed · {crewLabel}</Text>
              <Text style={s.crewCardSub}>You bring your own helper(s)</Text>
            </View>
          </View>
        )}

        {/* Certificate of insurance (owner, 28 Oct 2026). On the board it is a heads-up before
            accepting; once the job is theirs the driver can upload their own COI here too. */}
        {job.status !== 'COMPLETED' && job.status !== 'CANCELLED' && (
          <CoiCard jobId={job.id} summary={job.coi} canUpload={isActive && !!(job.driverId || job.assignedFleetDriverId || job.legMode)} />
        )}

        {/* Freight paperwork (owner, 3 Nov 2026: a Miami driver asked "Where can I find the FedEx
            documents that need to print out?"). The link was only plain text inside Item Details;
            this is one tap to the BOL, packing slip, receipt and pallet labels. */}
        {!!(job as any).freightDocsUrl && (
          <TouchableOpacity style={s.crewCard} activeOpacity={0.8} onPress={() => Linking.openURL((job as any).freightDocsUrl)}>
            <View style={s.crewIconWrap}>
              <Ionicons name="print-outline" size={20} color={RD.maroon} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={s.crewCardTitle}>Print the freight paperwork</Text>
              <Text style={s.crewCardSub}>Bill of lading, packing slip, receipt and pallet labels. Print before pickup.</Text>
            </View>
            <Ionicons name="open-outline" size={18} color={RD.textSec} />
          </TouchableOpacity>
        )}

        {/* Item Description — specs, dimensions, weight, precautions */}
        {job.itemDescription && (
          <View style={[s.stairsCard, { backgroundColor: RD.infoSoft, borderColor: RD.infoSoft }]}>
            <View style={s.stairsHeader}>
              <Ionicons name="cube-outline" size={16} color={RD.info} />
              <Text style={[s.stairsTitle, { color: RD.info }]}>Item Details</Text>
            </View>
            <Text style={[s.stairsText, { color: RD.textSec }]}>{job.itemDescription}</Text>
          </View>
        )}

        {/* (flag-gated · ADD C) Inline "How to move it" — top 3 steps, with a
            "See all steps" link that opens the full Helpful Hints sheet. */}
        {PREDISPATCH_ENABLED && stepList.length > 0 && (
          <View style={s.howToCard}>
            <View style={s.howToHead}>
              <Ionicons name="list-outline" size={16} color={RD.info} />
              <Text style={s.howToTitle}>How to move it</Text>
            </View>
            {stepList.slice(0, 3).map((tip: string, i: number) => (
              <View key={i} style={[s.howToStepRow, i > 0 && { marginTop: 11 }]}>
                <View style={s.howToStepNum}><Text style={s.howToStepNumText}>{i + 1}</Text></View>
                <Text style={s.howToStepText}>{tip}</Text>
              </View>
            ))}
            {stepList.length > 3 && (
              <TouchableOpacity
                style={s.howToSeeAll}
                activeOpacity={0.7}
                onPress={openTips}
              >
                <Text style={s.howToSeeAllText}>See all steps</Text>
                <Ionicons name="chevron-forward" size={15} color={RD.info} />
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Order Notes — delivery notes, stairs, obstacles, buyer instructions */}
        {job.deliveryNotes && (
          <View style={s.stairsCard}>
            <View style={s.stairsHeader}>
              <Ionicons name="document-text-outline" size={16} color={RD.amber} />
              <Text style={s.stairsTitle}>Order Notes</Text>
            </View>
            <Text style={s.stairsText}>{(() => {
              const raw = (job.deliveryNotes as string).replace(/\s*\|\s*/g, '\n');
              const keywords = /stair|step|flight|floor|elevator|lift|obstacle|narrow|tight|hallway|doorway|walkway|garage|basement|attic|gate|fence|parking|loading dock|freight|ramp|curb|gravel|driveway|hoist|crane|balcony|patio|deck|backyard|front door|back door|side door|entrance/i;
              const lines = raw.split('\n').map(l => l.trim()).filter(Boolean);
              const relevant = lines.filter(line => keywords.test(line));
              return relevant.length > 0 ? relevant.join('\n') : raw;
            })()}</Text>
          </View>
        )}

        {/* Privacy notice — contact info hidden before acceptance */}
        {!isActive && (
          <View style={s.privacyNotice}>
            <Ionicons name="shield-checkmark-outline" size={14} color={RD.textFaint} />
            <Text style={s.privacyNoticeText}>Contact details visible after accepting the job</Text>
          </View>
        )}

      </ScrollView>

      {/* Bottom Actions */}
      <View style={s.bottomBar}>
        {job.status === 'COMPLETED' ? (
          <TouchableOpacity style={s.primaryBtn} onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={18} color="#fff" />
            <Text style={s.primaryBtnText}>Back to History</Text>
          </TouchableOpacity>
        ) : isActive ? (
          <>
            <TouchableOpacity
              style={s.primaryBtn}
              onPress={() => navigation.navigate('ActiveJob', { jobId: job.id, job })}
            >
              <Ionicons name="navigate" size={18} color="#fff" />
              <Text style={s.primaryBtnText}>Go to Active Job</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={s.secondaryBtn}
              onPress={() => navigation.navigate('Messaging', { jobId: job.id, job })}
            >
              <Ionicons name="chatbubble-outline" size={18} color={RD.textSec} />
              <Text style={s.secondaryBtnText}>Messages</Text>
            </TouchableOpacity>
          </>
        ) : (
          <TouchableOpacity style={s.acceptBtn} onPress={handleAcceptPress} disabled={accepting} activeOpacity={0.85}>
            {accepting ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={s.acceptBtnText}>Accept Job</Text>
            )}
          </TouchableOpacity>
        )}
      </View>

      {/* Training Gate Modal */}
      {/* Pickup-truck heavy-rain acknowledgement — informational, lets the driver proceed. */}
      <Modal visible={!!wxConfirm} transparent animationType="fade" onRequestClose={() => setWxConfirm(null)}>
        <View style={s.modalOverlay}>
          <TouchableOpacity style={s.modalBackdrop} onPress={() => setWxConfirm(null)} activeOpacity={1} />
          <View style={[s.modalSheet, { paddingBottom: 28 }]}>
            <View style={s.modalHandle} />
            <View style={{ alignItems: 'center', paddingHorizontal: 22 }}>
              <Text style={{ fontSize: 44, marginBottom: 6 }}>{wxConfirm?.severe ? '⛈️' : '🌧️'}</Text>
              <Text style={{ fontSize: 19, fontWeight: '800', color: '#21303f', textAlign: 'center', marginBottom: 8 }}>Heavy rain expected</Text>
              <Text style={{ fontSize: 14, color: '#52677c', textAlign: 'center', lineHeight: 21, marginBottom: 16 }}>
                {wxConfirm ? `${wxConfirm.reason.charAt(0).toUpperCase() + wxConfirm.reason.slice(1)} is forecast during this job's ${wxConfirm.when} time.` : ''} Since you drive a pickup truck (open bed), please make sure you have <Text style={{ fontWeight: '800', color: '#21303f' }}>tarps and/or a cover</Text> to protect the item from the rain. You can still take the job.
              </Text>
              <TouchableOpacity onPress={() => { setWxConfirm(null); doAccept(); }} activeOpacity={0.85}
                style={{ backgroundColor: '#000', borderRadius: 14, paddingVertical: 15, width: '100%', alignItems: 'center', marginBottom: 10 }}>
                <Text style={{ color: '#fff', fontSize: 15.5, fontWeight: '800' }}>Got it — I have coverings, accept job</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setWxConfirm(null)} activeOpacity={0.7} style={{ paddingVertical: 8 }}>
                <Text style={{ color: '#8aa0b6', fontSize: 14, fontWeight: '600' }}>Back</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={showTrainingGate} transparent animationType="slide" onRequestClose={() => setShowTrainingGate(false)}>
        <View style={s.modalOverlay}>
          <TouchableOpacity style={s.modalBackdrop} onPress={() => setShowTrainingGate(false)} activeOpacity={1} />
          <View style={s.modalSheet}>
            <View style={s.modalHandle} />
            <View style={s.gateContent}>
              <View style={s.gateIconWrap}>
                <Ionicons name="school" size={30} color={RD.maroon} />
              </View>
              <Text style={s.gateTitle}>Certification Required</Text>
              <Text style={s.gateDesc}>
                Complete the <Text style={{ fontWeight: '700' }}>{categoryCertified && palletizingMissing ? 'Palletizing' : categoryLabel}</Text> training course to accept this job.
              </Text>
              <Text style={s.gatePayout}>${Math.round(job.payoutAmount || 0)} payout after certification</Text>
            </View>
            <View style={s.gateBtns}>
              <TouchableOpacity style={s.gateCancelBtn} onPress={() => setShowTrainingGate(false)}>
                <Text style={s.gateCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={s.gateConfirmBtn} onPress={handleGoToTraining}>
                <Ionicons name="school" size={17} color="#fff" style={{ marginRight: 6 }} />
                <Text style={s.gateConfirmText}>Go to Training</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Full-size image (single product photo) — swipe/rotate/zoom */}
      <ImageLightbox images={job.productImages || []} index={showImageModal && job.productImages?.[0] ? 0 : null} onClose={() => setShowImageModal(false)} />

      {/* Combined gallery lightbox — swipeable, rotateable, zoomable */}
      <ImageLightbox images={galleryLightbox?.list || []} index={galleryLightbox ? galleryLightbox.index : null} onClose={() => setGalleryLightbox(null)} />

      {/* Fleet Assignment Modal */}
      <Modal visible={showFleetAssign} transparent animationType="slide" onRequestClose={() => { setShowFleetAssign(false); if (reassignMode) { setReassignMode(false); setAcceptedJob(null); } else if (acceptedJob) { navigation.navigate('ActiveJob', { jobId: acceptedJob.id, job: acceptedJob }); } }}>
        <View style={{ flex: 1, justifyContent: 'flex-end' }}>
          <TouchableOpacity style={{ ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.5)' }} onPress={() => { setShowFleetAssign(false); if (reassignMode) { setReassignMode(false); setAcceptedJob(null); } else if (acceptedJob) { navigation.navigate('ActiveJob', { jobId: acceptedJob.id, job: acceptedJob }); } }} activeOpacity={1} />
          <View style={{ backgroundColor: RD.surface, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 24, paddingBottom: 40, maxHeight: '85%' }}>
            <View style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: RD.g15, alignSelf: 'center', marginBottom: 16 }} />
            <Text style={{ fontSize: 20, fontWeight: '700', color: RD.text, marginBottom: 4 }}>Assign Driver</Text>
            <Text style={{ fontSize: 13, color: RD.textFaint, marginBottom: 16 }}>{acceptedJob?.productTitle || 'Job'}</Text>
            {/* 🚨 CAPPING THE LIST DID NOT CAP THE SHEET, AND NORMAN STILL COULD NOT REACH
                THE TOP OF IT.
                The 8 Sep fix put the members in a ScrollView capped at half of
                `Dimensions.get('window').height`. The LIST then behaved - but the sheet
                around it did not: it is anchored to the BOTTOM of a flex:1 parent, so
                header + list + spinner + Cancel + 64px of padding still added up past the
                screen and the overflow went off the TOP. Norman's screenshot on 11 Sep has
                no grab handle, no "Assign Driver" title and no "Myself" row in it at all -
                they are above the notch. He is not failing to scroll a list; he is looking
                at a sheet whose top does not exist.
                Also: a Dimensions read is a number taken once, against a viewport that is
                not the same as this sheet's parent on an installed PWA. Bounding the SHEET
                at 85% and letting the list flexShrink into what is left is resolved by the
                layout engine against the real parent, on both native and web, and needs no
                number at all. */}
            <ScrollView style={{ flexShrink: 1 }} contentContainerStyle={{ paddingBottom: 2 }} showsVerticalScrollIndicator>
              <TouchableOpacity
                style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 14, backgroundColor: RD.g03, borderWidth: 1, borderColor: RD.border, marginBottom: 8 }}
                onPress={() => handleFleetAssign('self')}
                disabled={assigningFleet}
              >
                <Ionicons name="person-circle" size={28} color={RD.textSec} />
                <Text style={{ fontSize: 15, fontWeight: '600', color: RD.text, flex: 1 }}>Myself</Text>
                <Ionicons name="chevron-forward" size={16} color={RD.g30} />
              </TouchableOpacity>
              {fleetMembers.map((m: any) => (
                <TouchableOpacity
                  key={m.id}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 14, backgroundColor: RD.g03, borderWidth: 1, borderColor: RD.border, marginBottom: 8 }}
                  onPress={() => handleFleetAssign(m.id)}
                  disabled={assigningFleet}
                >
                  <Ionicons name="person" size={24} color={RD.textFaint} />
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 15, fontWeight: '600', color: RD.text }}>{m.name}</Text>
                    {m.phone && <Text style={{ fontSize: 12, color: RD.textFaint }}>{m.phone}</Text>}
                  </View>
                  <Ionicons name="chevron-forward" size={16} color={RD.g30} />
                </TouchableOpacity>
              ))}
            </ScrollView>
            {assigningFleet && <ActivityIndicator size="small" color={RD.textFaint} style={{ marginTop: 8 }} />}
            <TouchableOpacity onPress={() => { setShowFleetAssign(false); if (reassignMode) { setReassignMode(false); setAcceptedJob(null); } else if (acceptedJob) { navigation.navigate('ActiveJob', { jobId: acceptedJob.id, job: acceptedJob }); } }} style={{ alignSelf: 'center', padding: 12, marginTop: 8 }}>
              <Text style={{ color: RD.textFaint, fontWeight: '600' }}>{reassignMode ? 'Cancel' : 'Skip — Assign Later'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Driver Tips FAB — flashing lightbulb until viewed */}
      {hasHints && (
        <Animated.View style={{ position: 'absolute', bottom: 100, right: 20, width: 48, height: 48, borderRadius: 24, backgroundColor: RD.info, justifyContent: 'center', alignItems: 'center', shadowColor: '#000', shadowOpacity: 0.25, shadowRadius: 8, shadowOffset: { width: 0, height: 3 }, elevation: 5, zIndex: 10, opacity: !tipsViewed ? tipsPulse : 1 }}>
          <TouchableOpacity
            style={{ width: 48, height: 48, borderRadius: 24, justifyContent: 'center', alignItems: 'center' }}
            onPress={openTips}
            activeOpacity={0.8}
          >
            <Ionicons name="bulb-outline" size={22} color="#fff" />
          </TouchableOpacity>
        </Animated.View>
      )}

      {/* Helpful Hints Modal — v5 styled bottom sheet */}
      {hasHints && (
        <Modal visible={showTips} transparent animationType="slide" onRequestClose={() => { setShowTips(false); setShowDetailedTips(false); }}>
          <View style={s.hintsOverlay}>
            <TouchableOpacity style={StyleSheet.absoluteFill} onPress={() => { setShowTips(false); setShowDetailedTips(false); }} activeOpacity={1} />
            <View style={s.hintsSheet}>
              {/* Sheet header — info-blue bulb + title */}
              <View style={s.hintsHeader}>
                <View style={s.hintsHeaderIcon}>
                  <Ionicons name="bulb" size={19} color={RD.info} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={s.hintsHeaderTitle}>Helpful Hints</Text>
                  <Text style={s.hintsHeaderSub}>This item first, then the category guide</Text>
                </View>
                <TouchableOpacity onPress={() => { setShowTips(false); setShowDetailedTips(false); }} style={s.hintsCloseBtn} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }} activeOpacity={0.7}>
                  <Ionicons name="close" size={18} color={RD.textSec} />
                </TouchableOpacity>
              </View>

              <ScrollView style={{ flex: 1 }} contentContainerStyle={s.hintsBody} showsVerticalScrollIndicator={false}>
                {/* Item first, category second: components/LightbulbBody.tsx. */}
                <LightbulbBody job={job} />
              </ScrollView>
            </View>
          </View>
        </Modal>
      )}
    </View>
  );
}

function SectionCard({ title, icon, iconColor, children }: { title: string; icon: any; iconColor: string; children: React.ReactNode }) {
  return (
    <View style={s.sectionCard}>
      <View style={s.sectionHeader}>
        <Ionicons name={icon} size={18} color={iconColor} />
        <Text style={s.sectionTitle}>{title}</Text>
      </View>
      {children}
    </View>
  );
}

function InfoRow({ icon, value, onPress }: { icon: string; value?: string; onPress?: () => void }) {
  const iconName = (icon + '-outline') as any;
  const content = (
    <View style={s.infoRow}>
      <Ionicons name={iconName} size={16} color={RD.textFaint} />
      <Text style={[s.infoValue, onPress && s.infoLink]}>{value ?? '—'}</Text>
      {onPress && <Ionicons name="chevron-forward" size={14} color={RD.g30} />}
    </View>
  );
  if (onPress) return <TouchableOpacity onPress={onPress}>{content}</TouchableOpacity>;
  return content;
}

// Shared "warm premium" card surface — RD.surface w/ soft border + subtle shadow.
const card = {
  backgroundColor: RD.surface,
  borderRadius: 18,
  borderWidth: 1,
  borderColor: RD.border,
  padding: 16,
  shadowColor: '#000',
  shadowOpacity: 0.04,
  shadowRadius: 3,
  shadowOffset: { width: 0, height: 1 },
  elevation: 1,
} as const;

const s = StyleSheet.create({
  container:      { flex: 1, backgroundColor: RD.bg },
  loadingWrap:    { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: RD.bg },

  // Header
  header:         { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: spacing.lg, paddingTop: Platform.OS === 'ios' ? 56 : 36, paddingBottom: spacing.md, backgroundColor: RD.surface, borderBottomWidth: 1, borderBottomColor: RD.border },
  backBtn:        { padding: 4, minWidth: 44, minHeight: 44, justifyContent: 'center', alignItems: 'center' },
  headerTitleWrap:{ flex: 1, alignItems: 'center' },
  headerTitle:    { fontSize: 19, fontWeight: '700', color: RD.text },
  headerSub:      { fontSize: 12, color: RD.textFaint, marginTop: 1 },

  content:        { padding: spacing.lg, gap: 14, paddingBottom: 140 },

  // Top card
  topCard:        { ...card },
  statusBadge:    { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', borderRadius: 999, paddingHorizontal: 11, paddingVertical: 5, gap: 6, marginBottom: spacing.md },
  statusDot:      { width: 7, height: 7, borderRadius: 4 },
  statusText:     { fontSize: 11.5, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.3 },
  titleRow:       { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: spacing.sm },
  productThumb:   { width: 64, height: 64, borderRadius: 12, backgroundColor: RD.g05 },
  productTitle:   { fontSize: 21, fontWeight: '700', color: RD.text },
  productSub:     { fontSize: 13, color: RD.textSec, marginTop: -2, marginBottom: 8 },
  imageModalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.9)', justifyContent: 'center', alignItems: 'center' },
  imageModalImg:  { width: '90%', height: '70%' },
  pillRow:        { flexDirection: 'row', gap: 7, alignItems: 'center', flexWrap: 'wrap' },
  categoryPill:   { backgroundColor: RD.maroonSoft, borderRadius: 999, paddingHorizontal: 11, paddingVertical: 5 },
  categoryText:   { color: RD.maroon, fontSize: 11.5, fontWeight: '600', textTransform: 'uppercase' },
  crewPill:       { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: RD.g03, borderRadius: 999, paddingHorizontal: 11, paddingVertical: 5 },
  crewPillText:   { color: RD.textSec, fontSize: 11.5, fontWeight: '600' },
  distanceBadge:  { flexDirection: 'row', alignItems: 'center', gap: 4 },
  distanceText:   { fontSize: 12, color: RD.textFaint, fontWeight: '500' },
  windowHint:     { fontSize: 11.5, color: RD.textFaint, marginTop: 8 },

  // Crew card — headcount only; driver brings own helpers
  crewCard:       { ...card, flexDirection: 'row', alignItems: 'center', gap: 12 },
  crewIconWrap:   { width: 40, height: 40, borderRadius: 20, backgroundColor: RD.maroonSoft, justifyContent: 'center', alignItems: 'center' },
  crewCardTitle:  { fontSize: 15, fontWeight: '700', color: RD.text },
  crewCardSub:    { fontSize: 12.5, color: RD.textSec, marginTop: 1 },

  // Payout card — total only, light row card (never a fee breakdown)
  payoutCard:     { ...card, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  payoutLabelInline: { fontSize: 14.5, color: RD.textSec },
  payoutAmountInline: { fontSize: 24, fontWeight: '800', color: RD.money },

  // Section cards
  sectionCard:    { ...card },
  sectionHeader:  { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: spacing.md },
  sectionTitle:   { fontSize: 14, fontWeight: '700', color: RD.text },
  infoRow:        { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: RD.g05 },
  infoValue:      { flex: 1, fontSize: 14, color: RD.textSec, fontWeight: '500' },
  infoLink:       { color: RD.info },

  // Bottom bar
  bottomBar:      { position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: RD.surface, padding: spacing.lg, paddingBottom: Platform.OS === 'ios' ? 36 : 20, gap: 10, borderTopWidth: 1, borderTopColor: RD.border },
  primaryBtn:     { backgroundColor: RD.ink, borderRadius: 16, height: 54, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 8 },
  primaryBtnText: { color: '#fff', fontWeight: '600', fontSize: 16 },
  secondaryBtn:   { backgroundColor: RD.surface, borderWidth: 1.5, borderColor: RD.borderStrong, borderRadius: 16, height: 54, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 6 },
  secondaryBtnText: { color: RD.textSec, fontWeight: '600', fontSize: 15 },
  // Big v5 Accept button
  acceptBtn:      { backgroundColor: RD.ink, borderRadius: 18, height: 64, flexDirection: 'row', justifyContent: 'center', alignItems: 'center' },
  acceptBtnText:  { color: '#fff', fontWeight: '700', fontSize: 18 },

  // Training gate modal
  modalOverlay:   { flex: 1, justifyContent: 'flex-end' },
  modalBackdrop:  { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.5)' },
  modalSheet:     { backgroundColor: RD.surface, borderTopLeftRadius: radii.xxl, borderTopRightRadius: radii.xxl, padding: spacing.xl, paddingBottom: 40 },
  modalHandle:    { width: 36, height: 4, borderRadius: 2, backgroundColor: RD.g15, alignSelf: 'center', marginBottom: spacing.lg },
  gateContent:    { alignItems: 'center', paddingVertical: spacing.lg },
  gateIconWrap:   { width: 60, height: 60, borderRadius: 30, backgroundColor: RD.maroonSoft, justifyContent: 'center', alignItems: 'center', marginBottom: spacing.sm },
  gateTitle:      { fontSize: 22, fontWeight: '700', color: RD.text, marginBottom: 4 },
  gateDesc:       { fontSize: 15, color: RD.textSec, textAlign: 'center', lineHeight: 22, marginTop: 6, paddingHorizontal: spacing.base },
  gatePayout:     { fontSize: 14, fontWeight: '700', color: RD.money, marginTop: spacing.md },
  gateBtns:       { flexDirection: 'row', gap: 12, marginTop: spacing.lg },
  gateCancelBtn:  { flex: 1, height: 54, borderRadius: 16, backgroundColor: RD.surface, borderWidth: 1.5, borderColor: RD.borderStrong, justifyContent: 'center', alignItems: 'center' },
  gateCancelText: { color: RD.textSec, fontWeight: '600', fontSize: 15 },
  gateConfirmBtn: { flex: 2, height: 54, borderRadius: 16, backgroundColor: RD.maroon, flexDirection: 'row', justifyContent: 'center', alignItems: 'center' },
  gateConfirmText:{ color: '#fff', fontWeight: '600', fontSize: 15 },

  // Privacy notice
  privacyNotice:  { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: spacing.md, paddingVertical: 10 },
  privacyNoticeText: { fontSize: 12, color: RD.textFaint, fontWeight: '500' },
  stairsCard:     { padding: 14, borderRadius: 18, backgroundColor: RD.amberSoft, borderWidth: 1, borderColor: RD.amberSoft },
  stairsHeader:   { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 },
  stairsTitle:    { fontSize: 14, fontWeight: '700', color: RD.amber, fontFamily: fonts.heavy },
  stairsText:     { fontSize: 13, color: RD.textSec, lineHeight: 19 },

  // Helpful Hints sheet (v5)
  hintsOverlay:   { flex: 1, backgroundColor: 'rgba(15,10,12,0.45)', justifyContent: 'flex-end' },
  hintsSheet:     { backgroundColor: RD.bg, borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: '88%', overflow: 'hidden' },
  hintsHeader:    { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 18, paddingVertical: 14, backgroundColor: RD.surface, borderBottomWidth: 1, borderBottomColor: RD.border },
  hintsHeaderIcon:{ width: 34, height: 34, borderRadius: 10, backgroundColor: RD.infoSoft, justifyContent: 'center', alignItems: 'center' },
  hintsHeaderTitle:{ fontSize: 16, fontWeight: '700', color: RD.text },
  hintsHeaderSub: { fontSize: 11.5, color: RD.textFaint, marginTop: 1 },
  hintsCloseBtn:  { width: 32, height: 32, borderRadius: 16, backgroundColor: RD.g05, justifyContent: 'center', alignItems: 'center' },
  hintsBody:      { padding: 18, paddingBottom: 44 },
  hintsCallout:   { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 14, borderRadius: 14, backgroundColor: RD.amberSoft, borderWidth: 1, borderColor: RD.amberSoft, marginBottom: 18 },
  hintsCalloutText:{ flex: 1, fontSize: 13.5, fontWeight: '700', color: RD.amber, lineHeight: 19 },
  hintsSection:   { marginBottom: 16 },
  hintsSectionHead:{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 9 },
  hintsSectionTitle:{ fontSize: 13.5, fontWeight: '700', color: RD.text },
  hintsCard:      { backgroundColor: RD.surface, borderRadius: 14, borderWidth: 1, borderColor: RD.border, padding: 15 },
  hintsBodyText:  { fontSize: 13, color: RD.textSec, lineHeight: 20 },
  hintsStepRow:   { flexDirection: 'row', gap: 11, alignItems: 'flex-start' },
  hintsStepNum:   { width: 22, height: 22, borderRadius: 11, backgroundColor: RD.infoSoft, justifyContent: 'center', alignItems: 'center' },
  hintsStepNumText:{ fontSize: 11.5, fontWeight: '700', color: RD.info },
  hintsStepText:  { flex: 1, fontSize: 13.5, color: RD.textSec, lineHeight: 19, paddingTop: 1 },
  hintsLearnMore: { marginBottom: 16, paddingVertical: 12, paddingHorizontal: 16, borderRadius: 14, backgroundColor: RD.amberSoft, borderWidth: 1, borderColor: RD.amberSoft, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 },
  hintsLearnMoreText:{ fontSize: 15, fontWeight: '700', color: RD.amber },
  hintsDetailHead:{ fontSize: 13, fontWeight: '800', color: RD.amber, letterSpacing: 0.3, marginBottom: 3 },
  hintsVideoRow:  { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, backgroundColor: RD.surface, borderWidth: 1, borderColor: RD.border, borderRadius: 14, marginBottom: 8 },
  hintsVideoText: { flex: 1, fontSize: 14, fontWeight: '600', color: RD.text },

  // (flag-gated) ADD A — Helpful Hints reminder card
  hintsReminderCard: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 18, backgroundColor: RD.infoSoft, borderWidth: 1, borderColor: RD.info + '30' },
  hintsReminderIcon: { width: 38, height: 38, borderRadius: 12, backgroundColor: RD.surface, justifyContent: 'center', alignItems: 'center' },
  hintsReminderTitle:{ fontSize: 14.5, fontWeight: '700', color: RD.text },
  hintsReminderSub:  { fontSize: 12, color: RD.textSec, marginTop: 1 },

  // (flag-gated) ADD B — combined gallery card + lightbox
  galleryCard:     { ...card },
  galleryCardHead: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 },
  galleryCardTitle:{ fontSize: 14, fontWeight: '700', color: RD.text },
  galleryCardHint: { fontSize: 11.5, color: RD.textFaint, fontWeight: '500' },
  galleryThumb:    { width: 116, height: 116, borderRadius: 12, marginHorizontal: 4, backgroundColor: RD.g05 },
  galleryZoomHint: { position: 'absolute', bottom: 6, right: 10, backgroundColor: 'rgba(0,0,0,0.55)', borderRadius: 10, padding: 3 },
  galleryTag:      { position: 'absolute', top: 6, left: 10, backgroundColor: 'rgba(0,0,0,0.6)', borderRadius: 8, paddingHorizontal: 6, paddingVertical: 2 },
  galleryTagText:  { color: '#fff', fontSize: 9, fontWeight: '700', letterSpacing: 0.2 },
  glOverlay:       { flex: 1, backgroundColor: 'rgba(0,0,0,0.92)' },
  glPage:          { width: WIN.width, height: '100%', justifyContent: 'center', alignItems: 'center' },
  glImage:         { width: WIN.width * 0.92, height: WIN.height * 0.72 },
  glCloseHint:     { position: 'absolute', top: Platform.OS === 'ios' ? 60 : 40, alignSelf: 'center', flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 20, paddingHorizontal: 14, paddingVertical: 8, zIndex: 5 },
  glCloseText:     { color: '#fff', fontSize: 13, fontWeight: '600' },
  glCounter:       { position: 'absolute', bottom: Platform.OS === 'ios' ? 60 : 40, alignSelf: 'center', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 20, paddingHorizontal: 14, paddingVertical: 7 },
  glCounterText:   { color: '#fff', fontSize: 12, fontWeight: '600' },

  // (flag-gated) ADD C — inline "How to move it"
  howToCard:       { ...card },
  howToHead:       { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 },
  howToTitle:      { fontSize: 14, fontWeight: '700', color: RD.text },
  howToStepRow:    { flexDirection: 'row', gap: 11, alignItems: 'flex-start' },
  howToStepNum:    { width: 22, height: 22, borderRadius: 11, backgroundColor: RD.infoSoft, justifyContent: 'center', alignItems: 'center' },
  howToStepNumText:{ fontSize: 11.5, fontWeight: '700', color: RD.info },
  howToStepText:   { flex: 1, fontSize: 13.5, color: RD.textSec, lineHeight: 19, paddingTop: 1 },
  howToSeeAll:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4, marginTop: 14, paddingVertical: 10, borderRadius: 12, backgroundColor: RD.infoSoft },
  howToSeeAllText: { fontSize: 13.5, fontWeight: '700', color: RD.info },
});
